import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import Header from '@/components/Header';
import OrganisationHeader from '@/components/organisation/OrganisationHeader';
import OrganisationTabs, { MerchantType, OrgTabType } from '@/components/organisation/OrganisationTabs';
import OrganisationOverview from '@/components/organisation/OrganisationOverview';
import OrganisationReportsTab from '@/components/organisation/OrganisationReportsTab';
import OrganisationDataTab from '@/components/organisation/OrganisationDataTab';
import OrganisationFilesTab from '@/components/organisation/OrganisationFilesTab';
import OrganisationInventoryTab from '@/components/organisation/OrganisationInventoryTab';
import OrganisationBillingTab from '@/components/organisation/OrganisationBillingTab';
import OrganisationPurchaseOrdersTab from '@/components/organisation/OrganisationPurchaseOrdersTab';
import OrganisationLoading from '@/components/organisation/OrganisationLoading';
import OrganisationError from '@/components/organisation/OrganisationError';
import { MerchantSettingsTab } from '@/components/organisation/settings/MerchantSettingsTab';
import { MerchantActionsRow } from '@/components/organisation/settings/actions/MerchantActionsRow';
import { DeleteMerchantDangerZone } from '@/components/organisation/settings/actions/DeleteMerchantDangerZone';
import { useAuth } from '@/context/AuthContext';
import { useAdminMode } from '@/hooks/useAdminMode';
import { ExportNotificationsProvider } from '@/components/data-table/ExportNotificationsProvider';
import {
  fetchAvailableTables,
  findDefaultTable,
} from '@/utils/api';
import { FilterOperation } from '@/hooks/useConnectionData';

const EMBEDDINGS_REFRESH_DAYS = 7;

const OrganisationProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const { search } = useLocation();
  const [loading, setLoading] = useState(true);
  const [organisation, setOrganisation] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<OrgTabType>('overview');
  // Inventory sub-tab to open when another tab links there (FIG-321 Variant Mapping summary).
  const [inventoryInitialTab, setInventoryInitialTab] = useState<'variant-mapping' | undefined>(undefined);
  const [availableTables, setAvailableTables] = useState<any[]>([]);
  const [defaultTableName, setDefaultTableName] = useState<string | null>(null);
  const [defaultTableSchema, setDefaultTableSchema] = useState<string>('public');
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [connectionType, setConnectionType] = useState<string | null>(null);
  const [queryResults, setQueryResults] = useState<any[] | null>(null);
  const [tableInfo, setTableInfo] = useState<{ schema: string; tableName: string } | null>(null);
  const [queryFilters, setQueryFilters] = useState<Array<{ column: string; value: string; operation: FilterOperation }> | null>(null);
  const [embeddingsProgress, setEmbeddingsProgress] = useState<{
    total: number;
    processed: number;
    succeeded: string[];
    failed: string[];
  }>({ total: 0, processed: 0, succeeded: [], failed: [] });
  const dataTableRef = useRef<HTMLDivElement>(null);
  const isDraftStatus = new URLSearchParams(search).get('status') === 'draft';
  // Admin-only controls (pens, actions) follow the signed-in user's role (FIG-320),
  // and on the Settings tab only show while admin mode is on (FIG-345 item 7).
  const { isAdmin, user } = useAuth();
  const { adminMode, canEnable: canEnableAdminMode, setAdminMode } = useAdminMode(id, {
    isAdmin,
    userEmail: user?.email,
    userId: user?.id,
    organisationName: organisation?.name,
  });

  const clearQueryFilters = useCallback(() => {
    setQueryFilters(null);
    setQueryResults(null);
    setTableInfo(null);
  }, []);

  const shouldGenerateEmbeddings = useCallback((org: any) => {
    if (!org.embeddings_generated_at) {
      return true;
    }

    const lastGenerated = new Date(org.embeddings_generated_at);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - lastGenerated.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays > EMBEDDINGS_REFRESH_DAYS;
  }, []);

  const fetchOrganisationDetails = useCallback(async (draftId: string | null = null) => {
    try {
      setLoading(true);

      if (!id) {
        throw new Error('Organisation ID or Draft ID is required');
      }

      const recordId = id;

      const { data, error } = await supabase
        .from('organisations')
        .select('*')
        .eq('id', recordId)
        .maybeSingle();

      if (error) {
        console.error('[fetchOrganisationDetails] Error:', error);
        throw error;
      }

      if (data) {
        setOrganisation(data);
      } else {
        throw new Error(`Organisation or Draft ${recordId} not found`);
      }
    } catch (error: any) {
      console.error('[fetchOrganisationDetails] Error fetching data:', error);
      setError(error.message || 'Failed to load data');
      toast({
        title: 'Error',
        description: 'Failed to load organisation or draft details',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [id, toast, isDraftStatus]);

  // Settings saves refresh the merchant in place: no loading screen, so the
  // Settings tab keeps its open groups and any other edit in progress.
  const refreshOrganisationInBackground = useCallback(async () => {
    if (!id) return;
    const { data, error } = await supabase.from('organisations').select('*').eq('id', id).maybeSingle();
    if (error || !data) {
      console.error('[refreshOrganisationInBackground] Error:', error);
      toast({
        title: 'Could not refresh the merchant',
        description: 'Your change was saved. Reload the page to see the latest details.',
        variant: 'destructive',
      });
      return;
    }
    setOrganisation(data);
  }, [id, toast]);

  const handleConnectionChange = useCallback(async (newConnectionId: string) => {
    if (newConnectionId === connectionId) {
      return;
    }

    // Clear all table-related state when switching connections
    clearQueryFilters();
    setConnectionId(newConnectionId);

    // Reset all table data immediately
    setAvailableTables([]);
    setDefaultTableName(null);
    setDefaultTableSchema('public');
    setQueryResults(null);
    setTableInfo(null);
    setQueryFilters(null);

    // If empty connection ID, don't try to load tables
    if (!newConnectionId) {
      setConnectionType(null);
      return;
    }

    if (organisation?.connections) {
      const connectionEntries = Object.entries(organisation.connections) as [string, any][];
      const connectionInfo = connectionEntries.find(([key, conn]) =>
        conn.connectionId === newConnectionId || key === newConnectionId
      )?.[1] as any;

      if (connectionInfo) {
        const isCsvConnection = connectionInfo.csvMetadata !== undefined;

        if (isCsvConnection) {
          setConnectionType('CSV');
        } else {
          const connName = connectionInfo.name || '';
          if (connName.includes('Shopify')) {
            setConnectionType('Shopify');
          } else if (connName.includes('Walmart')) {
            setConnectionType('Walmart Marketplace');
          } else if (connName.includes('Amazon')) {
            setConnectionType('Amazon Selling');
          } else {
            setConnectionType(connName); // Default to Shopify if unknown
          }
        }
      }
    }
  }, [connectionId, clearQueryFilters, organisation]);

  useEffect(() => {
    fetchOrganisationDetails();
  }, [fetchOrganisationDetails]);

  useEffect(() => {
    const loadAvailableTables = async () => {
      if (!connectionId) {
        return;
      }

      try {
        // Find the connection details from organisation to pass to fetchAvailableTables
        let connection = null;
        if (organisation?.connections) {
          const connections = Array.isArray(organisation.connections)
            ? organisation.connections
            : Object.entries(organisation.connections).map(([key, conn]: [string, any]) => ({
              ...conn,
              connectionId: conn.connectionId || key || conn.id,
            }));

          // First try direct match on parent connection
          connection = connections.find((conn: any) =>
            conn.connectionId === connectionId ||
            conn.id === connectionId ||
            conn.connectorId === connectionId
          );

          // If not found, search within csvImports arrays for expanded entries
          // This handles individual CSV imports that are expanded from a parent connection
          if (!connection) {
            for (const conn of connections as any[]) {
              if (conn.csvImports && Array.isArray(conn.csvImports)) {
                const csvMatch = conn.csvImports.find(
                  (csv: any) => csv.id === connectionId
                );
                if (csvMatch) {
                  // Construct a connection object with csvImport details merged
                  connection = {
                    ...conn,
                    id: csvMatch.id,
                    connectionId: csvMatch.id,
                    file_name: csvMatch.fileName,
                  };
                  console.log(`Found CSV import match for ${connectionId}:`, connection);
                  break;
                }
              }
            }
          }
        }

        // fetchAvailableTables now returns tables filtered by connectionId and includes registryData
        // For test clones, pass source org's ID so snapshot table names resolve correctly
        const effectiveOrgId = (organisation?.is_test_clone && organisation?.source_organisation_id)
          ? organisation.source_organisation_id
          : undefined;
        const tables = await fetchAvailableTables(connectionId, id, connection, true, effectiveOrgId);

        setAvailableTables(tables);

        setEmbeddingsProgress(prev => ({
          ...prev,
          total: tables.filter(table =>
            table.table_schema !== 'pg_catalog' &&
            table.table_schema !== 'information_schema' &&
            !table.table_name.includes('_junction_') &&
            !table.table_name.startsWith('pg_')
          ).length
        }));

        // findDefaultTable will automatically select the correct table for this connection
        // based on filtered tables (which only include tables for this connectionId)
        const defaultTable = findDefaultTable(tables, connectionId);
        if (defaultTable) {
          setDefaultTableName(defaultTable.tableName);
          setDefaultTableSchema(defaultTable.schema);
        }
      } catch (error) {
        console.error('Error fetching available tables:', error);
      }
    };

    loadAvailableTables();
  }, [connectionId, organisation, id]);

  // useEffect(() => {
  //   const generateAllEmbeddings = async () => {
  //     if (!connectionId || processingEmbeddings || !availableTables.length || embeddingsInitiated || !organisation) {
  //       return;
  //     }

  //     const shouldGenerate = shouldGenerateEmbeddings(organisation);

  //     if (!shouldGenerate) {
  //       return;
  //     }

  //     try {
  //       setEmbeddingsInitiated(true);
  //       setProcessingEmbeddings(true);

  //       toast({
  //         title: 'Generating Embeddings',
  //         description: `Starting to generate embeddings for all tables...`,
  //         variant: 'default',
  //       });

  //       const result = await generateEmbeddingsForAllTables(connectionId, availableTables);

  //       setEmbeddingsProgress(prev => ({
  //         ...prev,
  //         processed: result.succeeded.length + result.failed.length,
  //         succeeded: result.succeeded,
  //         failed: result.failed
  //       }));

  //       if (result.succeeded.length > 0 && id) {

  //         let updateSucceeded = false;
  //         let retryCount = 0;

  //         while (!updateSucceeded && retryCount < MAX_TIMESTAMP_UPDATE_RETRIES) {
  //           updateSucceeded = await updateEmbeddingsTimestamp(id);

  //           if (updateSucceeded) {
  //             await fetchOrganisationDetails();

  //             toast({
  //               title: 'Embeddings Generation Complete',
  //               description: `Generated embeddings for ${result.succeeded.length} tables successfully.`,
  //               variant: 'default',
  //             });
  //             break;
  //           }

  //           retryCount++;
  //           if (retryCount < MAX_TIMESTAMP_UPDATE_RETRIES) {
  //             await new Promise(resolve => setTimeout(resolve, 1000));
  //           }
  //         }

  //         if (!updateSucceeded) {
  //           console.error('[generateAllEmbeddings] All timestamp update attempts failed');
  //           await fetchOrganisationDetails();
  //         }
  //       }

  //       if (result.failed.length > 0) {
  //         console.error(`[generateAllEmbeddings] Failed to generate embeddings for ${result.failed.length} tables.`);
  //       }
  //     } catch (error: any) {
  //       console.error('[generateAllEmbeddings] Error generating embeddings:', error);
  //     } finally {
  //       setProcessingEmbeddings(false);
  //     }
  //   };

  //   generateAllEmbeddings();
  // }, [availableTables, connectionId, processingEmbeddings, toast, embeddingsInitiated, organisation, shouldGenerateEmbeddings, id, fetchOrganisationDetails]);

  if (loading) {
    return <OrganisationLoading />;
  }

  if (error || !organisation) {
    return <OrganisationError error={error} />;
  }

  // Filter connections to separate data connections (DB + CSV) from file imports
  const getDataConnections = () => {
    if (!organisation?.connections) return {};

    const dataConnections: any = {};
    Object.entries(organisation.connections).forEach(([key, value]: [string, any]) => {
      // Include database connections and CSV imports, exclude file imports
      if (value.service !== 'file' && value.type !== 'file' && !value.id?.startsWith('file-import-')) {
        dataConnections[key] = value;
      }
    });

    return dataConnections;
  };

  // Check if there are files (file imports)
  const hasFiles = () => {
    if (!organisation?.connections) return false;

    return Object.values(organisation.connections).some((conn: any) =>
      conn.service === 'file' || conn.type === 'file' || conn.id?.startsWith('file-import-')
    );
  };

  const dataConnections = getDataConnections();
  const hasConnections = Object.keys(dataConnections).length > 0;
  const hasFileImports = hasFiles();

  return (
    <ExportNotificationsProvider connectionId={connectionId} enabled={hasConnections}>
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <OrganisationHeader organisation={organisation} showStatus={isAdmin} />


          <OrganisationTabs
            activeTab={activeTab}
            setActiveTab={(tab) => {
              setInventoryInitialTab(undefined);
              setActiveTab(tab);
            }}
            hasConnections={hasConnections}
            hasFiles={hasFileImports}
            isAdmin={isAdmin}
            merchantType={(organisation.merchant_type || 'direct') as MerchantType}
            isTestClone={!!organisation.is_test_clone}
          />

          {activeTab === 'overview' && (
            <OrganisationOverview organisation={organisation} onConnectionRemoved={() => fetchOrganisationDetails()} onOpenSettings={() => setActiveTab('settings')} />
          )}

          {activeTab === 'reports' && (
            <OrganisationReportsTab organisation={organisation} onOpenSettings={() => setActiveTab('settings')} isAdmin={isAdmin} />
          )}

          {activeTab === 'data' && (
            <div ref={dataTableRef}>
              <OrganisationDataTab
                organisation={organisation}
                connectionData={{
                  connectorId: connectionId || '',
                  connectionId,
                  availableTables,
                  defaultSchema: defaultTableSchema,
                  defaultTableName,
                  queryResults,
                  selectedTableOverride: tableInfo,
                  queryFilters
                }}
                onClearQueryFilters={clearQueryFilters}
                onConnectionChange={handleConnectionChange}
              />
            </div>
          )}
          {activeTab === 'inventory' && (
            <OrganisationInventoryTab
              organisation={organisation}
              onOrganisationUpdate={() => fetchOrganisationDetails()}
              initialTab={inventoryInitialTab}
              onOpenSettings={() => setActiveTab('settings')}
            />
          )}

          {activeTab === 'billing' && (
            <OrganisationBillingTab organisation={organisation} />
          )}

          {activeTab === 'files' && (
            <OrganisationFilesTab organisation={organisation} />
          )}

          {activeTab === 'settings' && (
            <MerchantSettingsTab
              merchant={organisation}
              connections={Object.values(dataConnections)}
              canEdit={isAdmin && adminMode}
              isAdmin={isAdmin}
              adminMode={isAdmin ? { enabled: adminMode, onChange: setAdminMode, disabled: !canEnableAdminMode } : undefined}
              onUpdated={refreshOrganisationInBackground}
              onOpenInventory={() => setActiveTab('inventory')}
              onOpenVariantMapping={() => {
                setInventoryInitialTab('variant-mapping');
                setActiveTab('inventory');
              }}
              actions={isAdmin && adminMode ? (
                <MerchantActionsRow merchant={organisation} onUpdated={refreshOrganisationInBackground} />
              ) : null}
              // Delete merchant stays visible to admins whether admin mode is on or off (FIG-345).
              footer={isAdmin ? <DeleteMerchantDangerZone merchant={organisation} /> : null}
            />
          )}

          {activeTab === 'purchase-orders' && organisation.merchant_type === 'reseller' && (
            <OrganisationPurchaseOrdersTab organisation={organisation} isAdmin={isAdmin} />
          )}
        </main >
      </div >
    </ExportNotificationsProvider >
  );
};

export default OrganisationProfile;
